import { JSX, useEffect, useState } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { GetCareSignature } from "@/utils/utils";
import { useSignature } from "@/context/SignatureContext";
import { Tooltip } from "./Tooltip";
import { User } from "@/pages";
import * as Popover from "@radix-ui/react-popover";
import Button from "./Common/Button";
import { Switch } from "radix-ui";
import { SuccessModal } from "./SuccessModal";

type FormField = {
  id: string;
  label: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  tooltip?: string;
};

const formFlow = [
  {
    id: "fullName",
    label: "Full Name",
    type: "text",
    required: true,
    placeholder: "Enter your full name",
    tooltip: "Enter your full name as it should appear in your email signature",
  },
  {
    id: "jobTitle",
    label: "Role Title",
    type: "text",
    required: true,
    placeholder: "Enter your role title at (no abbreviations please)",
    tooltip: "Your official role title at Care Access (please avoid abbreviations)",
  },
  {
    id: "timezone",
    label: "Timezone",
    type: "text",
    required: false,
    placeholder: "E.g EST - Remote | EST - Boston, MA | EST - New York, NY",
    tooltip: "Your timezone and location, e.g., 'EST - Remote' or 'EST - Boston, MA'",
  },
  {
    id: "academicTitle",
    label: "Academic Titles/Credentials",
    type: "text",
    required: false,
    placeholder: "E.g. MD, PhD, MBA, etc.",
    tooltip: "Your academic credentials, separated by commas if multiple",
  },
  {
    id: "phoneNumber",
    label: "Phone Number",
    type: "text",
    placeholder: "Enter your phone number",
    tooltip: "Your work phone number in the format: +1 (XXX) XXX-XXXX",
  },
  {
    id: "faxNumber",
    label: "Fax Number",
    type: "text",
    required: false,
    placeholder: "Enter your fax number",
    tooltip: "Your fax number in the format: +1 (XXX) XXX-XXXX",
  },
  {
    id: "meetingLink",
    label: "Schedule Meeting Link",
    type: "text",
    required: false,
    placeholder: "Enter your schedule meeting link",
    tooltip: "Your personal meeting scheduling link (e.g., Calendly)",
  },
  {
    id: "emailSignOff",
    label: "Email Sign-off",
    type: "text",
    required: false,
    placeholder: "E.g Best, Regards, etc.",
    tooltip: "How you want to end your emails, e.g., 'Best regards' or 'Kind regards'",
  },
] as const satisfies readonly FormField[]; // Strict type checking

const InputElement = ({
  id,
  label,
  type,
  required = false,
  placeholder,
  tooltip,
  field,
}: FormField & {
  field: any; // TanStack Form field
}): JSX.Element => {
  const error = field.state.meta.errors?.[0];

  return (
    <label className="text-textDefault" htmlFor={id}>
      <div className="flex items-center gap-2 mb-2.5 leading-1">
        <div>
          {label}
          {required && <span className="text-sunrise500"> *</span>}
        </div>
        {tooltip && (
          <Tooltip content={tooltip}>
            <svg
              className="w-4 h-4 text-gray-400 hover:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </Tooltip>
        )}
      </div>
      <div className="w-full">
        <Popover.Root open={!!error}>
          <Popover.Trigger className="w-full" tabIndex={-1}>
            <input
              className={`bg-white px-3 py-2 border-1 ${
                error ? "border-red-700" : "border-slate-300"
              } focus:border-dusk500 rounded focus:outline-none focus:ring-0 w-full text-textDefault placeholder:text-[#dfdfdf] text-lg`}
              type={type}
              name={id}
              id={id}
              disabled={id === "jobTitle" ? true : false}
              required={required}
              placeholder={placeholder}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              aria-required={required}
              aria-invalid={!!error}
              aria-describedby={error ? `${id}-error` : undefined}
            />
          </Popover.Trigger>
          {error && (
            <Popover.Content
              className="z-50 flex items-center gap-x-1 bg-red-100 -mt-1 px-4 py-3 border border-red-700 rounded-sm font-semibold text-red-700 text-sm"
              sideOffset={5}
              id={`${id}-error`}
              tabIndex={-1}
              onOpenAutoFocus={(e) => e.preventDefault()}
              onPointerDownOutside={(e) => e.preventDefault()}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {error}
            </Popover.Content>
          )}
        </Popover.Root>
      </div>
    </label>
  );
};

const NoImage = () => {
  return (
    <div className="flex max-md:flex-col items-center gap-6">
      <img className="mx-auto min-w-[80px]" src="/images/profile-placeholder.svg" alt="" />
      <div>
        <h3 className="font-[450] text-textFiller text-base">
          You don't have a profile picture set
        </h3>
        <p className="text-textFiller text-sm">
          To update your profile photo
          <a
            className="text-motion600 underline"
            href="https://myaccount.microsoft.com/?ref=MeControl"
            target="_blank"
          >
            {" "}
            click here
          </a>{" "}
          to go directly to your profile page and change it there. Once updated, please sign out and
          back in again to make use of the new photo.
        </p>
      </div>
    </div>
  );
};

export const Sidebar = ({ user }: { user: User }): JSX.Element => {
  const { setSignature } = useSignature();
  const [usePhoto, setUsePhoto] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const phoneNumber =
    user.businessPhones && user.businessPhones.length
      ? user.businessPhones[0]
      : user.mobilePhone || "";

  const form = useForm({
    defaultValues: {
      fullName: user.displayName || "",
      jobTitle: user.jobTitle || "",
      phoneNumber,
      faxNumber: "",
      timezone: "",
      meetingLink: "",
      academicTitle: "",
      emailSignOff: "",
    },
    onSubmitInvalid: ({ value }) => {
      console.log(value);
    },
    onSubmit: async ({ value }) => {
      const signature = GetCareSignature(
        value.fullName || "",
        value.jobTitle || "",
        value.phoneNumber || "",
        value.faxNumber || "",
        user.mail,
        value.timezone || "",
        value.meetingLink || "",
        value.academicTitle || "",
        usePhoto && user.photoUrl ? user.photoUrl : "",
        value.emailSignOff || ""
      );
      setSignature(signature);

      try {
        const blobInput = new Blob([signature], { type: "text/html" });
        const clipboardItemInput = new window.ClipboardItem({ "text/html": blobInput });

        await navigator.clipboard.write([clipboardItemInput]);
        setIsSuccessModalOpen(true);
      } catch (err) {
        console.error("Failed to copy signature: ", err);
      }
    },
    validators: {
      onSubmit: ({ value }) => {
        const requiredFields = ["fullName", "jobTitle"] as const;
        const hasEmptyRequiredFields = requiredFields.some(
          (fieldId) => !value[fieldId as keyof typeof value]
        );
        if (hasEmptyRequiredFields) {
          return "Please fill in all required fields";
        }
        return undefined;
      },
    },
  });

  const validateField = (field: FormField, value: string) => {
    if (field.required && !value) {
      return "This field is required";
    }
    return undefined;
  };

  useEffect(() => {
    const signaturePreview = GetCareSignature(
      user.displayName || "",
      user.jobTitle || "", // jobTitle
      phoneNumber, // phoneNumber
      "", // faxNumber
      user.mail,
      "", // timezone
      "", // meetingLink
      "", // academicTitle
      "", // photo
      "" // emailSignOff
    );
    setSignature(signaturePreview);
  }, [user, setSignature]);

  // Subscribe to all form values
  const values = useStore(form.store, (state) => state.values);

  useEffect(() => {
    const photo = usePhoto && user.photoUrl ? user.photoUrl : "";
    const signature = GetCareSignature(
      values.fullName,
      values.jobTitle,
      values.phoneNumber,
      values.faxNumber,
      user.mail,
      values.timezone,
      values.meetingLink,
      values.academicTitle,
      photo,
      values.emailSignOff
    );
    setSignature(signature);
  }, [values, setSignature, user, usePhoto]);

  return (
    <div className="w-full min-w-[350px] md:max-w-[400px]">
      <div className="bg-slate-50 shadow-md p-4 rounded-xl">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="gap-4 grid"
        >
          {formFlow.map((formItem) => (
            <form.Field
              key={formItem.id}
              name={formItem.id}
              validators={{
                onChange: ({ value }) => {
                  return validateField(formItem, value);
                },
                onBlur: ({ value }) => {
                  return validateField(formItem, value);
                },
              }}
              children={(field) => <InputElement {...formItem} field={field} />}
            />
          ))}
          {user.photoUrl ? (
            <div className="flex items-center gap-3">
              <img src={user.photoUrl} alt="User" className="rounded-full w-20 h-20" />
              <div className="flex items-center gap-2">
                <Switch.Root
                  className="relative bg-slate-400 data-[state=checked]:bg-stateActive rounded-full outline-none w-[42px] h-[25px] cursor-default"
                  id="usePhoto"
                  checked={usePhoto}
                  onCheckedChange={() => setUsePhoto(!usePhoto)}
                  style={{ WebkitTapHighlightColor: "rgba(0, 0, 0, 0)" }}
                >
                  <Switch.Thumb className="block bg-white rounded-full size-[21px] transition-transform translate-x-0.5 data-[state=checked]:translate-x-[19px] duration-100 will-change-transform" />
                </Switch.Root>
                <div className="Label" style={{ paddingRight: 15 }}>
                  Use Photo in signature
                </div>
              </div>
            </div>
          ) : (
            <NoImage />
          )}
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                className="justify-center"
                variant="classic"
                type="submit"
                disabled={!canSubmit || isSubmitting}
              >
                {isSubmitting ? "Processing..." : "COPY SIGNATURE CONTENTS"}
              </Button>
            )}
          />
        </form>
      </div>
      <SuccessModal isOpen={isSuccessModalOpen} setIsOpen={setIsSuccessModalOpen} />
    </div>
  );
};
